import React, { useState, useEffect, useCallback } from 'react';
import Api from '../../Auth/Api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Modal, Button } from 'react-bootstrap';
import { decodeJWT } from '../../Components/jwtUtils';

function Logs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_records: 0,
        limit: 20,
        has_next_page: false,
        has_prev_page: false
    });
    const [filters, setFilters] = useState({
        page: 1,
        limit: 20,
        search: '',
        data_inicio: '',
        data_fim: ''
    });
    
    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;
    
    // Determinar título e descrição baseado no nível
    const isMeusLogs = nivel === 'Gestor' || nivel === 'Aluno';
    const titulo = isMeusLogs ? 'Meus Logs' : 'Logs do Sistema';
    const descricao = isMeusLogs 
        ? 'Visualize o histórico das suas ações realizadas no sistema.' 
        : 'Visualize o histórico de ações realizadas pelos usuários no sistema.';

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: filters.page,
                limit: filters.limit,
                search: filters.search,
                data_inicio: filters.data_inicio,
                data_fim: filters.data_fim
            });

            const response = await Api.get(`/api/listar-logs?${params}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setLogs(response.data.logs);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Erro ao carregar logs:', error);
            if (error.response?.status === 403) {
                alert('Acesso negado. Você não tem permissão para visualizar estes logs.');
            } else {
                alert('Erro ao carregar logs. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    }, [filters, token]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setFilters(prev => ({ ...prev, page: 1, limit: newLimit }));
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, page: 1, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilters({
            page: 1,
            limit: 20,
            search: '',
            data_inicio: '',
            data_fim: ''
        });
    };

    const handleShowDetails = (log) => {
        setSelectedLog(log);
        setShowModal(true);
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
        } catch (error) {
            return dateString;
        }
    };

    const truncateText = (text, maxLength = 100) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <>
            <div className="fade-in w-100">
                <span className="text-primary fs-1">{titulo}</span>
                <p className="fs-6">{descricao}</p>

                {/* Filtros */}
                <div className="card mb-4">
                    <div className="card-body">
                        <h6 className="card-title mb-3">Filtros</h6>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label htmlFor="search" className="form-label">Pesquisar</label>
                                <input
                                    type="text"
                                    id="search"
                                    className="form-control"
                                    placeholder="Buscar por ação, usuário ou código..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="data_inicio" className="form-label">Data Início</label>
                                <input
                                    type="date"
                                    id="data_inicio"
                                    className="form-control"
                                    value={filters.data_inicio}
                                    onChange={(e) => handleFilterChange('data_inicio', e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <label htmlFor="data_fim" className="form-label">Data Fim</label>
                                <input
                                    type="date"
                                    id="data_fim"
                                    className="form-control"
                                    value={filters.data_fim}
                                    onChange={(e) => handleFilterChange('data_fim', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="mt-3 text-end">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleClearFilters}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Limpar Filtros
                            </button>
                        </div>
                    </div>
                </div>

                {/* Controles de paginação */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                        <label htmlFor="limit" className="mb-0">Itens por página:</label>
                        <select
                            id="limit"
                            className="form-select"
                            style={{ width: 'auto' }}
                            value={filters.limit}
                            onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="text-muted">
                        Total: {pagination.total_records} registros
                    </div>
                </div>

                {loading && (
                    <div className="d-flex justify-content-center my-5">
                        <div className="loader"></div>
                    </div>
                )}

                {!loading && logs.length > 0 && (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Usuário</th>
                                    <th>Código</th>
                                    <th>Ação</th>
                                    <th>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => (
                                    <tr key={log.id}>
                                        <td>
                                            <small className="text-muted">
                                                {formatDate(log.data)}
                                            </small>
                                        </td>
                                        <td>
                                            <strong>{log.nome_usuario || 'Usuário removido'}</strong>
                                        </td>
                                        <td>
                                            <code className="text-secondary">
                                                {log.codigo_usuario || 'N/A'}
                                            </code>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div title={log.acao} className="flex-grow-1">
                                                    {truncateText(log.acao, 60)}
                                                </div>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleShowDetails(log)}
                                                    title="Ver detalhes"
                                                >
                                                    <i className="bi bi-eye"></i>
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                {log.ip || 'N/A'}
                                            </small>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && logs.length === 0 && (
                    <div className="text-center my-5">
                        <i className="bi bi-journal-x display-1 text-muted"></i>
                        <p className="text-muted mt-3">Nenhum log encontrado.</p>
                    </div>
                )}

                {/* Paginação */}
                {pagination.total_pages > 1 && (
                    <nav aria-label="Navegação de páginas" className="mt-4">
                        <ul className="pagination justify-content-center">
                            <li className={`page-item ${!pagination.has_prev_page ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                    disabled={!pagination.has_prev_page}
                                >
                                    <i className="bi bi-chevron-left"></i>
                                </button>
                            </li>
                            
                            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                                .filter(page => {
                                    const current = pagination.current_page;
                                    const total = pagination.total_pages;
                                    return page === 1 || page === total || 
                                           (page >= current - 2 && page <= current + 2);
                                })
                                .map((page, index, array) => {
                                    if (index > 0 && array[index - 1] !== page - 1) {
                                        return (
                                            <li key={`ellipsis-${page}`} className="page-item disabled">
                                                <span className="page-link">...</span>
                                            </li>
                                        );
                                    }
                                    return (
                                        <li key={page} className={`page-item ${page === pagination.current_page ? 'active' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </li>
                                    );
                                })}
                            
                            <li className={`page-item ${!pagination.has_next_page ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                    disabled={!pagination.has_next_page}
                                >
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </li>
                        </ul>
                    </nav>
                )}

                {/* Modal de Detalhes */}
                <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Detalhes do Log</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedLog && (
                            <div className="row">
                                <div className="col-md-6">
                                    <h6>Informações do Usuário</h6>
                                    <table className="table table-sm">
                                        <tbody>
                                            <tr>
                                                <td><strong>Nome:</strong></td>
                                                <td>{selectedLog.nome_usuario || 'Usuário removido'}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Código:</strong></td>
                                                <td><code>{selectedLog.codigo_usuario || 'N/A'}</code></td>
                                            </tr>
                                            <tr>
                                                <td><strong>Data/Hora:</strong></td>
                                                <td>{formatDate(selectedLog.data)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>IP:</strong></td>
                                                <td><code>{selectedLog.ip || 'N/A'}</code></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="col-md-6">
                                    <h6>Ação Realizada</h6>
                                    <div className="border rounded p-3 bg-light">
                                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {selectedLog.acao}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Fechar
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
}

export default Logs; 